import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CourseTemplateService } from '../../../services/course-template.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../../services/loading.service';
import { TrainingTypeModel } from '../../../models/training/training-type.model';
import { CourseTemplateFullModel } from 'src/app/models/course/course-template-full.model';
import {
  categoryIcon,
  clockIcon,
  playIcon,
  bookmarkIcon,
  bookmarkOutlinedIcon,
  calendarIcon,
} from '../../../app-icons';
import { DomSanitizer } from '@angular/platform-browser';
import {
  combineLatest,
  forkJoin,
  Observable,
  of,
  repeat,
  Subscription,
  throwError,
  timer,
} from 'rxjs';
import { CourseService } from '../../../services/course.service';
import { ToastrService } from 'ngx-toastr';
import { CourseContentTypeModel } from '../../../models/course/course-content-type.model';
import {
  catchError,
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { CourseParticipationStatusModel } from '../../../models/course/course-participation-status.model';
import { AuthorizationService } from '../../../services/authorization.service';
import { PersonEventFlagService } from 'src/app/services/person-event-flag.service';
import { TranslateService } from '@ngx-translate/core';
import { PersonService } from 'src/app/services/person.service';
import { LearningPathService } from 'src/app/services/learning-path.service';
import { CoursePersonStatusModel } from 'src/app/models/learning-path/learning-path-course.model';
import { ScormCrossWindowService } from 'src/app/services/scorm-cross-window.service';
import { UserContextService } from 'src/app/services/user-context.service';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { VideoPlayerComponent } from 'src/app/components/shared/video-player/video-player.component';

@Component({
  selector: 'app-course-info',
  templateUrl: './course-info.component.html',
  styleUrls: ['./course-info.component.scss'],
  standalone: false
})
export class CourseInfoComponent implements OnDestroy, AfterViewInit {
  @ViewChild('videoPlayer', { static: false })
  private videoPlayer!: VideoPlayerComponent;

  courseTemplateId: string;
  videoDuration: string | null = null;
  TrainingType = TrainingTypeModel;
  courseTemplate: CourseTemplateFullModel | null = null;
  participationStatus: CourseParticipationStatusModel =
    CourseParticipationStatusModel.NoParticipation;
  videoProgressSeconds: number = 0;
  isFromSubscribedSource: boolean = false;
  isClosed: boolean = false;
  isDeployed: boolean = false;
  showModal = false;
  isAdmin = false;
  isManager = false;
  isTrainingCatalogue = false;
  isLearningPath = false;
  isBookmarked = false;
  isStartRestrictedByLearningPath?: boolean;
  private subs = new Subscription();

  courseContentTypeModel = CourseContentTypeModel;
  courseParticipationStatus = CourseParticipationStatusModel;
  
  get trainingType() {
    switch (this.courseTemplate?.courseTemplate.trainingType) {
      case TrainingTypeModel.General:
        return 'General';
      case TrainingTypeModel.Compliance:
        return 'Compliance';
      case TrainingTypeModel.IDD:
        return 'IDD';
      default:
        return '';
    }
  }

  get trainingGroups() {
    if (!this.courseTemplate?.trainingGroups.length) {
      return '';
    } else {
      return this.courseTemplate?.trainingGroups
        .map((group) => group.name)
        .join(', ');
    }
  }

  get durationString() {
    if (
      !this.courseTemplate ||
      !this.courseTemplate?.courseTemplate ||
      typeof this.courseTemplate?.courseTemplate.iddTimeMinutes !== 'number' ||
      isNaN(this.courseTemplate?.courseTemplate.iddTimeMinutes)
    ) {
      return '00:00';
    }

    const hours = Math.floor(
      this.courseTemplate?.courseTemplate.iddTimeMinutes / 60
    );
    const remainingMinutes =
      this.courseTemplate?.courseTemplate.iddTimeMinutes % 60;
    const durationString = `${hours}:${
      remainingMinutes < 10 ? '0' : ''
    }${remainingMinutes}`;

    return durationString;
  }

  get trainingCategories() {
    if (
      this.courseTemplate &&
      this.courseTemplate.trainingCategories &&
      this.courseTemplate.trainingCategories.length
    ) {
      return this.courseTemplate.trainingCategories
        .map((category) => category.name)
        .join(', ');
    } else {
      return null;
    }
  }

  constructor(
    private route: ActivatedRoute,
    private courseTemplateService: CourseTemplateService,
    private personEventFlagService: PersonEventFlagService,
    private personService: PersonService,
    private learningPathService: LearningPathService,
    private loadingService: LoadingService,
    private router: Router,
    private sanitized: DomSanitizer,
    private courseService: CourseService,
    private authorizationService: AuthorizationService,
    private toastrService: ToastrService,
    public translateService: TranslateService,
    private scormCrossWindowService: ScormCrossWindowService,
    private userContext: UserContextService,
    private pageStateService: PageStateService
  ) {
    this.isTrainingCatalogue = !!this.route.snapshot.params['categoryId'];
    this.isLearningPath = !!this.route.snapshot.queryParams['learningPathId'];
    this.courseTemplateId = this.route.snapshot.params['id'];

    const role = this.userContext.currentRole;
    this.isAdmin = role === 'SuperAdmin';
    this.isManager = role === 'Manager';

    const obs = forkJoin([
      this.courseTemplateService.getCourseTemplate(this.courseTemplateId),
      this.courseService
        .getParticipationStatus(this.courseTemplateId)
        .pipe(
          catchError(() => of(CourseParticipationStatusModel.NoParticipation))
        ),
      this.courseService.getVideoProgress(this.courseTemplateId),
      this.personEventFlagService.getPersonEventFlags(this.courseTemplateId),
      this.personService.getLearningPathsAssigned(),
    ]);
    this.loadingService
      .load(obs)
      .subscribe(([
        courseTemplate, participationStatus, videoProgress, personEventFlags, personLearningPaths
      ]) => {
        this.courseTemplate = courseTemplate;
        this.videoProgressSeconds = videoProgress.progressSeconds;

        this.isFromSubscribedSource = courseTemplate.isFromSubscribedSource;
        this.isClosed = courseTemplate.courseTemplate.isClosed;
        this.isDeployed = courseTemplate.courseTemplate.isDeployed;
        
        var isCourseAssignedToLearningPath = courseTemplate.learningPaths.length > 0;

        if(isCourseAssignedToLearningPath) {
          var learningPathId = courseTemplate.learningPaths[0].learningPath.id;
          var isCurrentUserAssignedToLearningPath = personLearningPaths.findIndex(plp => plp.id == learningPathId) !== -1;

          if(isCurrentUserAssignedToLearningPath) {
            this.loadingService.load(this.learningPathService.getCoursePersonStatus(learningPathId, courseTemplate.courseTemplate.id))
              .subscribe((coursePersonStatusModel) => {
                if(coursePersonStatusModel === CoursePersonStatusModel.Required) {
                  this.isStartRestrictedByLearningPath = true;
                } else {
                  this.isStartRestrictedByLearningPath = false;
                }
              });
          } else {
            this.isStartRestrictedByLearningPath = false;
          }
        } else {
          this.isStartRestrictedByLearningPath = false;
        }

        this.participationStatus = participationStatus;
        this.isBookmarked = !!personEventFlags.find(
          (flag) => flag.actionFlag == 1
        );
      });

    const sub = timer(2500, 2500)
      .pipe(
        filter(
          () =>
            this.courseTemplate != null &&
            this.courseTemplate.courseTemplate.courseContentType ===
              CourseContentTypeModel.AzureVideo
        )
      )
      .subscribe(() => this.videoPlayer?.updatePlayTime());

    this.subs.add(sub);

    this.personEventFlagService.addPersonEventFlag(
      this.courseTemplateId,
      'Opened'
    ).subscribe();
  }

  toggleBookmark() {
    if (this.isBookmarked) {
      this.loadingService
        .load(
          this.personEventFlagService.deletePersonEventFlag(
            this.courseTemplateId,
            'Bookmark'
          )
        )
        .subscribe(() => {
          this.toastrService.success(
            this.translateService.instant(
              'TRAINING-CATALOGUE.UNBOOKMARK-SUCCESS'
            )
          );
          this.isBookmarked = false;
        });
    } else {
      this.loadingService
        .load(
          this.personEventFlagService.addPersonEventFlag(
            this.courseTemplateId,
            'Bookmark'
          )
        )
        .subscribe(() => {
          this.toastrService.success(
            this.translateService.instant('TRAINING-CATALOGUE.BOOKMARK-SUCCESS')
          );
          this.isBookmarked = true;
        });
    }
  }

  getPlayIcon() {
    return this.sanitized.bypassSecurityTrustHtml(playIcon);
  }

  getClockIcon() {
    return this.sanitized.bypassSecurityTrustHtml(clockIcon);
  }

  getCategoryIcon() {
    return this.sanitized.bypassSecurityTrustHtml(categoryIcon);
  }

  getBookmarkIcon() {
    return this.sanitized.bypassSecurityTrustHtml(bookmarkIcon);
  }

  getBookmarkOutlinedIcon() {
    return this.sanitized.bypassSecurityTrustHtml(bookmarkOutlinedIcon);
  }

  getCalendarIcon() {
    return this.sanitized.bypassSecurityTrustHtml(calendarIcon);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  handleStartCourse(): void {
    if (
      this.courseTemplate?.courseTemplate.courseContentType ==
      CourseContentTypeModel.AzureVideo
    ) {
      this.playVideo();
    } else if (
      this.courseTemplate?.courseTemplate.courseContentType ==
      CourseContentTypeModel.AzureScorm
    ) {
      this.startScorm();
    } else if (
      this.courseTemplate?.courseTemplate.courseContentType ==
      CourseContentTypeModel.External
    ) {
      this.startExternal();
    }
  }

  onStartCourseClick(isStart: boolean): void {
    if (isStart) {
      this.loadingService
        .load(this.courseService.setStatusStarted(this.courseTemplateId))
        .subscribe(() => {
          this.handleStartCourse();
        });
    } else {
      this.handleStartCourse();
    }
  }

  startScorm() {
    let scormWindow: Window | null = null;

    this.authorizationService
      .getCurrentPerson()
      .pipe(
        switchMap(person => {
          const url = this.router.serializeUrl(
            this.router.createUrlTree([`/scorm-player/${this.courseTemplateId}`], {
              queryParams: {
                personId: person.person.id,
                personName: `${person.person.firstName} ${person.person.lastName}`
              }
            })
          );

          scormWindow = window.open(
            url,
            'MyWindow',
            `width=${screen.availWidth}, height=${screen.availHeight}`
          );
          if (!scormWindow) {
            this.toastrService.error(
              'Could not start SCORM course. Please allow popup windows in your browser settings.'
            );
            return of(false);
          }
          return of(true);
        }),
        filter((windowOpened) => windowOpened),
        switchMap(() => this.scormCrossWindowService.isFinished$.pipe(take(1))),
        tap(() => {
          if (scormWindow && !scormWindow.closed) {
            scormWindow.close();
          }
        }),
        switchMap(() =>
          this.loadingService.load(
            this.courseService.getParticipationStatus(this.courseTemplateId)
          )
        ),
        tap(participationStatus => this.participationStatus = participationStatus),
        filter(
          participationStatus =>
            participationStatus === CourseParticipationStatusModel.Completed
        ),
        switchMap(() => this.completeOrRedirectToQuestions())
      )
      .subscribe(participationStatus => {
        console.log('SCORM finished, status:', participationStatus)
      });
  }

  playVideo(): void {
    this.videoPlayer.playVideo();
  }

  updateVideoProgress(seconds: number): void {
    this.courseService
      .trackVideoProgress(
        this.courseTemplateId,
        seconds
      )
      .subscribe();
  }

  public completeOrRedirectToQuestions(): Observable<void> {
    if (!this.courseTemplate) {
      this.toastrService.error('Error');
      return throwError(() => {});
    } else if (this.courseTemplate.questions.length > 0) {
      this.showModal = true;
      return of();
    } else {
      return this.loadingService
        .load(this.courseService.complete(this.courseTemplateId))
        .pipe(
          tap(() => {
            this.navigateQuestions(true);
          }),
          switchMap(() => of())
        );
    }
  }

  navigateQuestions(isSkip: boolean = false): void {
    if (isSkip) {
      this.router.navigate(
        ['my-trainings', 'course', this.courseTemplateId, 'questions'],
        { queryParams: { skipQuestions: true } }
      );
    } else {
      this.router.navigate([
        'my-trainings',
        'course',
        this.courseTemplateId,
        'questions',
      ]);
    }
  }

  navigateBack(): void {
    if (this.isTrainingCatalogue) {
      this.pageStateService.setFlag(
        PageStateFlags.RestoreTrainingCatalogueOverview
      );

      this.router.navigate([
        `/training-catalogue/${this.route.snapshot.params['categoryId']}`,
      ]);
    } 
    else if (this.isLearningPath) {
      this.router.navigate([
        `/learning-paths/${this.route.snapshot.queryParams['learningPathId']}`,
      ]);
    } else {
      this.pageStateService.setFlag(
        PageStateFlags.RestoreMyTrainingsOverviewTable
      );

      this.router.navigate(['/my-trainings']);
    }
  }

  protected readonly CourseContentTypeModel = CourseContentTypeModel;

  ngAfterViewInit(): void { }

  private startExternal() {
    const windowObs = this.loadingService
      .load(this.courseService.getExternalUrl(this.courseTemplateId))
      .pipe(
        switchMap((externalUrl) => {
          const myWindow = window.open(
            externalUrl,
            'MyWindow',
            'width=' + screen.availWidth + ',height=' + screen.availHeight
          );
          if (myWindow == null) {
            this.toastrService.error(
              'Could not start course. Please allow popup windows in your browser settings'
            );
          }
          return of(myWindow);
        }),
        filter((window) => window != null)
      );
    combineLatest([windowObs, timer(1000, 250)])
      .pipe(
        repeat(),
        filter(([w]) => w!.closed),
        take(1),
        switchMap(() =>
          this.loadingService.load(
            this.courseService.getParticipationStatus(this.courseTemplateId)
          )
        ),
        filter(
          (participationStatus) =>
            participationStatus == CourseParticipationStatusModel.Completed
        ),
        switchMap(() =>
          this.loadingService.load(
            this.courseService.complete(this.courseTemplateId)
          )
        )
      )
      .subscribe(() => this.completeOrRedirectToQuestions());
  }

  onAdminViewClick(): void {
    this.router.navigate(['manage-elearning', this.courseTemplateId]);
  }

  navigateInviteAttendees(): void {
    this.router.navigate([
      'elearning',
      'invite',
      this.courseTemplateId
    ], { state: { fromPage: this.router.url } });
  }

  protected readonly CourseParticipationStatusModel =
    CourseParticipationStatusModel;
}
